import { Organisation, OrganisationType, TradingEntity, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import { isNil } from 'lodash-es'
import { createInvalidValueValidationIssue } from 'model'
import { TradingRelationshipAttributes, TradingRelationshipSpreadsheetParseResult } from './parseTradingRelationshipSpreadsheet'

export enum TradingRelationshipFields {
  RELATIONSHIP_ACTION = 'Action',
  BUY_ORGANISATION = 'Buy Side Organisation',
  BUY_TRADING_ENTITY = 'Buy Side Trading Entity',
  SELL_ORGANISATION = 'Sell Side Organisation',
  SELL_TRADING_ENTITY = 'Sell Side Trading Entity',
  US_SPONSORED_CLEARING = 'US Sponsored Clearing',
  US_SPONSORED_CLEARING_DEFAULT = 'US SC Default',
  MASTER_LEGAL_AGREEMENTS = 'Master Legal Agreements',
  DEFAULT_AGREEMENT = 'Default Agreement'
}

export interface PendingTradingRelationship extends TradingRelationshipAttributes {
  idx: string
  buyTradingEntityId: string | null
  sellTradingEntityId: string | null
  validationIssues: ValidationIssue[]
}

export const validateTradingRelationshipParseResult = (
  parseResult: TradingRelationshipSpreadsheetParseResult,
  getTradingEntityByShortNameAndOrgShortCode: (orgShortCode: string, shortName: string) => TradingEntity | null,
  getOrganisationByShortCode: (orgShortCode: string) => Organisation | null
): PendingTradingRelationship[] => {
  const { tradingRelationships } = parseResult

  const tradingRelationshipIds: string[] = []
  const duplicateIds: string[] = []
  return tradingRelationships.map(
    (
      {
        relationshipAction,
        buyOrganisation,
        buyTradingEntity,
        sellOrganisation,
        sellTradingEntity,
        usSponsoredClearing,
        usSponsoredClearingDefault,
        masterLegalAgreements,
        defaultAgreement
      },
      idx
    ) => {
      const validationIssues: ValidationIssue[] = []

      if (isNil(buyOrganisation)) {
        validationIssues.push(
          createInvalidValueValidationIssue(TradingRelationshipFields.BUY_ORGANISATION, Locale.translate(Translation.SC_VALUE_REQUIRED))
        )
      }

      if (isNil(buyTradingEntity)) {
        validationIssues.push(
          createInvalidValueValidationIssue(TradingRelationshipFields.BUY_TRADING_ENTITY, Locale.translate(Translation.SC_VALUE_REQUIRED))
        )
      }

      if (isNil(sellOrganisation)) {
        validationIssues.push(
          createInvalidValueValidationIssue(TradingRelationshipFields.SELL_ORGANISATION, Locale.translate(Translation.SC_VALUE_REQUIRED))
        )
      }

      if (isNil(sellTradingEntity)) {
        validationIssues.push(
          createInvalidValueValidationIssue(TradingRelationshipFields.SELL_TRADING_ENTITY, Locale.translate(Translation.SC_VALUE_REQUIRED))
        )
      }

      const buyTradingEntityId = getTradingEntityId(buyOrganisation, buyTradingEntity, getTradingEntityByShortNameAndOrgShortCode)
      if (!isNil(buyOrganisation) && !isNil(buyTradingEntity) && isNil(buyTradingEntityId)) {
        validationIssues.push(
          createInvalidValueValidationIssue(
            TradingRelationshipFields.BUY_ORGANISATION,
            Locale.translate(Translation.SC_INVALID_TRADING_ENTITY, {
              shortName: buyTradingEntity,
              orgShortCode: buyOrganisation
            })
          ),
          createInvalidValueValidationIssue(
            TradingRelationshipFields.BUY_TRADING_ENTITY,
            Locale.translate(Translation.SC_INVALID_TRADING_ENTITY, {
              shortName: buyTradingEntity,
              orgShortCode: buyOrganisation
            })
          )
        )
      }
      if (
        !isNil(buyTradingEntityId) &&
        !isNil(buyOrganisation) &&
        !getOrganisationByShortCode(buyOrganisation)?.organisationTypes.includes(OrganisationType.BUY_SIDE)
      ) {
        validationIssues.push(
          createInvalidValueValidationIssue(
            TradingRelationshipFields.BUY_ORGANISATION,
            Locale.translate(Translation.SC_INVALID_BUY_ORGANISATION)
          )
        )
      }

      const sellTradingEntityId = getTradingEntityId(sellOrganisation, sellTradingEntity, getTradingEntityByShortNameAndOrgShortCode)
      if (!isNil(sellOrganisation) && !isNil(sellTradingEntity) && isNil(sellTradingEntityId)) {
        validationIssues.push(
          createInvalidValueValidationIssue(
            TradingRelationshipFields.SELL_ORGANISATION,
            Locale.translate(Translation.SC_INVALID_TRADING_ENTITY, {
              shortName: sellTradingEntity,
              orgShortCode: sellOrganisation
            })
          ),
          createInvalidValueValidationIssue(
            TradingRelationshipFields.SELL_TRADING_ENTITY,
            Locale.translate(Translation.SC_INVALID_TRADING_ENTITY, {
              shortName: sellTradingEntity,
              orgShortCode: sellOrganisation
            })
          )
        )
      }
      if (
        !isNil(sellTradingEntityId) &&
        !isNil(sellOrganisation) &&
        !getOrganisationByShortCode(sellOrganisation)?.organisationTypes.includes(OrganisationType.SELL_SIDE)
      ) {
        validationIssues.push(
          createInvalidValueValidationIssue(
            TradingRelationshipFields.SELL_ORGANISATION,
            Locale.translate(Translation.SC_INVALID_SELL_ORGANISATION)
          )
        )
      }

      if (!isNil(buyOrganisation) && !isNil(sellOrganisation) && buyOrganisation === sellOrganisation) {
        validationIssues.push(
          createInvalidValueValidationIssue(
            TradingRelationshipFields.BUY_ORGANISATION,
            Locale.translate(Translation.SC_SAME_ORGANISATION_ERROR)
          ),
          createInvalidValueValidationIssue(
            TradingRelationshipFields.SELL_ORGANISATION,
            Locale.translate(Translation.SC_SAME_ORGANISATION_ERROR)
          )
        )
      }

      if (isNil(defaultAgreement) && masterLegalAgreements.length > 0) {
        validationIssues.push(
          createInvalidValueValidationIssue(
            TradingRelationshipFields.DEFAULT_AGREEMENT,
            Locale.translate(Translation.SC_MISSING_DEFAULT_AGREEMENT)
          )
        )
      }
      if (!isNil(defaultAgreement) && !masterLegalAgreements.includes(defaultAgreement)) {
        validationIssues.push(
          createInvalidValueValidationIssue(
            TradingRelationshipFields.DEFAULT_AGREEMENT,
            Locale.translate(Translation.SC_INVALID_DEFAULT_AGREEMENT)
          )
        )
      }

      if (isNil(buyTradingEntityId) || isNil(sellTradingEntityId)) {
        return {
          idx: idx.toString(),
          buyTradingEntityId: null,
          sellTradingEntityId: null,
          relationshipAction,
          buyOrganisation,
          buyTradingEntity,
          sellOrganisation,
          sellTradingEntity,
          usSponsoredClearing,
          usSponsoredClearingDefault,
          masterLegalAgreements,
          defaultAgreement,
          validationIssues
        }
      }

      const rowTradingRelationshipId = `${buyTradingEntityId}-${sellTradingEntityId}`
      const isDuplicate = tradingRelationshipIds.includes(rowTradingRelationshipId)
      if (isDuplicate) {
        duplicateIds.push(rowTradingRelationshipId)
      } else {
        tradingRelationshipIds.push(rowTradingRelationshipId)
      }

      if (isDuplicate) {
        const errorMsg = Locale.translate(Translation.SC_DUPLICATE_TRADING_RELATIONSHIP, {
          buyOrganisation,
          buyTradingEntity,
          sellOrganisation,
          sellTradingEntity
        })
        validationIssues.push(
          createInvalidValueValidationIssue(TradingRelationshipFields.BUY_ORGANISATION, errorMsg),
          createInvalidValueValidationIssue(TradingRelationshipFields.BUY_TRADING_ENTITY, errorMsg),
          createInvalidValueValidationIssue(TradingRelationshipFields.SELL_ORGANISATION, errorMsg),
          createInvalidValueValidationIssue(TradingRelationshipFields.SELL_TRADING_ENTITY, errorMsg)
        )
      }

      return {
        idx: idx.toString(),
        buyTradingEntityId: isDuplicate ? null : buyTradingEntityId,
        sellTradingEntityId: isDuplicate ? null : sellTradingEntityId,
        relationshipAction,
        buyOrganisation,
        buyTradingEntity,
        sellOrganisation,
        sellTradingEntity,
        usSponsoredClearing,
        usSponsoredClearingDefault,
        masterLegalAgreements,
        defaultAgreement,
        validationIssues
      }
    }
  )
}

function getTradingEntityId(
  orgShortCode: string | null,
  shortName: string | null,
  getTradingEntityByShortNameAndOrgShortCode: (orgShortCode: string, shortName: string) => TradingEntity | null
): string | null {
  if (isNil(orgShortCode) || isNil(shortName)) {
    return null
  }
  return getTradingEntityByShortNameAndOrgShortCode(orgShortCode, shortName)?.id ?? null
}
